import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, formatINR } from "../lib/api";

export default function AdminCodSettings() {
    const [form, setForm] = useState(null);
    const [busy, setBusy] = useState(false);
    const [pinsText, setPinsText] = useState("");

    const refresh = () =>
        api.get("/admin/cod-settings").then(({ data }) => {
            setForm(data);
            setPinsText((data.allowedPincodes || []).join(", "));
        }).catch(() => setForm(null));
    useEffect(() => { refresh(); }, []);

    const save = async (e) => {
        e.preventDefault();
        if (!form) return;
        setBusy(true);
        const pincodes = pinsText.split(",").map((s) => s.trim()).filter(Boolean);
        try {
            await api.patch("/admin/cod-settings", {
                enabled: !!form.enabled,
                upfrontPercent: parseInt(form.upfrontPercent, 10),
                minOrderValueRupees: parseInt(form.minOrderValueRupees, 10),
                maxOrderValueRupees: parseInt(form.maxOrderValueRupees, 10),
                allowedPincodes: pincodes,
                cancelForfeitsAdvance: !!form.cancelForfeitsAdvance,
            });
            toast.success("COD settings saved");
            refresh();
        } catch (e2) { toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Failed"); }
        finally { setBusy(false); }
    };

    if (!form) return <div className="text-inkMuted">Loading…</div>;

    const exampleOrder = 1000;
    const exampleAdvance = Math.ceil(exampleOrder * (form.upfrontPercent || 0) / 100);

    return (
        <div data-testid="admin-cod-settings-page">
            <span className="label-eyebrow">Payments</span>
            <h1 className="mt-2 font-display text-4xl text-inkDark">Cash on Delivery</h1>
            <p className="mt-1 text-inkMuted max-w-2xl">Enable COD with a small upfront advance. Customers pay the advance via Razorpay at checkout, and the balance in cash on delivery.</p>

            <form onSubmit={save} className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5 max-w-4xl">
                <label className="flex items-center gap-3 md:col-span-2 bg-beige rounded-2xl px-4 py-3">
                    <input type="checkbox" checked={!!form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} data-testid="cod-enabled-toggle" />
                    <span className="font-medium">COD enabled</span>
                </label>
                <label className="block">
                    <span className="label-eyebrow">Upfront advance (%)</span>
                    <input type="number" min={0} max={100} value={form.upfrontPercent} onChange={(e) => setForm({ ...form, upfrontPercent: e.target.value })} className="mt-2 w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5" data-testid="cod-upfront-input" />
                </label>
                <label className="block">
                    <span className="label-eyebrow">Min order value (₹)</span>
                    <input type="number" min={0} value={form.minOrderValueRupees} onChange={(e) => setForm({ ...form, minOrderValueRupees: e.target.value })} className="mt-2 w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5" data-testid="cod-min-input" />
                </label>
                <label className="block">
                    <span className="label-eyebrow">Max order value (₹)</span>
                    <input type="number" min={0} value={form.maxOrderValueRupees} onChange={(e) => setForm({ ...form, maxOrderValueRupees: e.target.value })} className="mt-2 w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5" data-testid="cod-max-input" />
                </label>
                <label className="flex items-center gap-3 md:col-span-2">
                    <input type="checkbox" checked={!!form.cancelForfeitsAdvance} onChange={(e) => setForm({ ...form, cancelForfeitsAdvance: e.target.checked })} />
                    <span>Customer cancellation forfeits the advance (shown in policy copy)</span>
                </label>
                <label className="block md:col-span-2">
                    <span className="label-eyebrow">Allowed pincodes (comma-separated; blank = all India)</span>
                    <textarea rows={3} value={pinsText} onChange={(e) => setPinsText(e.target.value)} className="mt-2 w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5 font-mono text-xs" placeholder="e.g. 110001, 400001, 560001" data-testid="cod-pincodes-input" />
                </label>
                <div className="md:col-span-2 bg-skyblue/20 rounded-2xl p-4 text-sm">
                    <div className="label-eyebrow text-[10px]">Preview</div>
                    <div className="mt-1">Order {formatINR(exampleOrder)} → customer pays <strong>{formatINR(exampleAdvance)}</strong> upfront and <strong>{formatINR(exampleOrder - exampleAdvance)}</strong> on delivery.</div>
                </div>
                <div className="md:col-span-2">
                    <button type="submit" disabled={busy} className="btn-pill text-sm disabled:opacity-50" data-testid="cod-save-button">{busy ? "Saving…" : "Save COD settings"}</button>
                </div>
            </form>
        </div>
    );
}
